import { fadeIn, fadeOut } from "../utils/animate"
import Component from "./component"

/**
 * Toggles between the given elements when given event is triggered
 */
export default class SwitchingButton {
    /**
     * Element to attach class to
     */
    #element = null

    /**
     * Component to register the element to
     */
    #context = null
    
    /**
     * Signature of current active element
     */
    #elementSignature = null

    /**
     * Main element
     * - `signature` - unique identifier
     * - `callback` - callback function
     * - `mainIconSelector` - selector for the icon to look for with the main element
     * - `altIconSelector` - alternate selector for the icon to look for with the main element
     * - `title` - title of the element
     */
    #mainElementOptions = null

    /**
     * Alternate element. 
     * 
     * Exact same properties as `this.#mainElementOptions`
     */
    #altElementOptions = null

    constructor(selector, mainElementOptions, altElementOptions, regName, context = null, ignoreMissingElement = false, event = 'click') {
        this.#context = context || new Component()
        
        if (typeof selector !== 'string') {
            throw new Error('selector must be a string')
        }

        if (typeof regName !== 'string') {
            throw new Error('regName must be a string')
        }

        this.#validateElementsOptions(mainElementOptions)
        this.#validateElementsOptions(altElementOptions)

        if (typeof event !== 'string') {
            throw new Error('event must be a string')
        }

        this.#mainElementOptions = mainElementOptions
        this.#altElementOptions = altElementOptions
        
        try {
            this.#element = this.#context.getElementBySelector(selector)
            
            if (this.#element) {
                this.#element.addEventListener(event, this.#clickHandler)
                this.#context.registerNewElement(regName, this.#element)
                this.#init()
            }
        } catch (e) {
            if (!ignoreMissingElement) {
                throw e
            }
        }
    }

    #init = (fade = false) => {
        this.#elementSignature = null

        // Fontawesome icons are in place
        if (fade) {
            fadeOut(this.#altElementOptions.altIconSelector)
            fadeIn(this.#mainElementOptions.altIconSelector)
            return
        }

        // Otherwise, toggle i element placeholders
        this.#toggleIcon(this.#mainElementOptions.mainIconSelector)
        this.#toggleIcon(this.#altElementOptions.mainIconSelector, false)
    }

    #validateElementsOptions = (options) => {
        if (typeof options.signature !== 'string') {
            throw new Error('Both main and alternate elements\' signature must be a string')
        }

        if (typeof options.callback !== 'function') {
            throw new Error('Both main and alternate elements\' callback must be a function')
        }

        if (typeof options.mainIconSelector !== 'string') {
            throw new Error('Both main and alternate elements\' mainIconSelector must be a string')
        }

        if (typeof options.altIconSelector !== 'string') {
            throw new Error('Both main and alternate elements\' altIconSelector must be a string')
        }

        if (typeof options.title !== 'string') {
            throw new Error('Both main and alternate elements\' title must be a string')
        }
    }

    /**
     * Show/Hide icon without animation
     * 
     * @param {string} selector 
     * @param {boolean} show 
     */
    #toggleIcon = (selector, show = true) => {
        const icon = this.#context.getElementBySelector(selector, this.#element)
        icon.style.display = show ? 'block' : 'none'
    }

    /**
     * Switch active element and execute respective `onClick` handler
     */
    #clickHandler = (e) => {
        if (this.#elementSignature === this.#mainElementOptions.signature) {
            this.#elementSignature = this.#altElementOptions.signature
            this.#altElementOptions.callback(e)
        } else {
            this.#elementSignature = this.#mainElementOptions.signature
            this.#mainElementOptions.callback(e)
        }

        this.#statefulUpdate()
    }

    /**
     * Show/Hide icons with animation based on `this.#elementSignature`
     */
    #statefulUpdate = () => {
        switch (this.#elementSignature) {
            case this.#mainElementOptions.signature:
                fadeOut(this.#mainElementOptions.altIconSelector)
                fadeIn(this.#altElementOptions.altIconSelector)
                this.#element.setAttribute('title', this.#altElementOptions.title)
                this.#element.setAttribute('aria-label', this.#altElementOptions.title)
                break
            case this.#altElementOptions.signature:
            default:
                fadeOut(this.#altElementOptions.altIconSelector)
                fadeIn(this.#mainElementOptions.altIconSelector)
                this.#element.setAttribute('title', this.#mainElementOptions.title)
                this.#element.setAttribute('aria-label', this.#mainElementOptions.title)
                break
        }
    }

    /**
     * Return active element's signature
     */
    getElementSignature = () => {
        return this.#elementSignature
    }

    /**
     * Reset to initial state with animation
     */
    reset = () => {
        this.#init(true)
    }
}